<template>
  <div class="kanban">
    <!-- toolbar: search (left), card detail density control (right) -->
    <div class="kanban-toolbar">
      <LeadInput
        v-model="searchQuery"
        type="search"
        class="kanban-search"
        :placeholder="$t('orders.details.kanban.search_placeholder')">
        <template #prefix>
          <IconSearch :size="18" class="kanban-search-icon" />
        </template>
      </LeadInput>
      <CardDensityControl v-model="density" />
    </div>

    <!-- error state: initial load failed and there's nothing on screen -->
    <div v-if="!isLoading && loadError && visibleLanes.length === 0" class="kanban-empty-board">
      <p class="text-muted">{{ $t('orders.details.kanban.load_error') }}</p>
    </div>

    <!-- empty state: no swimlanes configured (or all are hidden) -->
    <div v-else-if="!isLoading && visibleLanes.length === 0" class="kanban-empty-board">
      <p class="text-muted">{{ $t('orders.details.kanban.no_swimlanes') }}</p>
    </div>

    <!-- board -->
    <div v-else class="kanban-board" :class="{ 'is-loading': isLoading }">
      <KanbanSwimlane
        v-for="lane in visibleLanes"
        :key="`${lane.lane_key}-${boardVersion}`"
        :lane="lane"
        :density="density"
        :is-loading="isLoading"
        v-model:collapsed="collapsedLanes[lane.lane_key]"
        @move="handleMove"
        @selection-change="handleSelectionChange" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { fetchCandidateBoard, moveCandidates } from '@/api/candidates';
import type { BoardLane, CardDensity } from '@/types/kanban';
import type { MoveEvent } from '@/components/orders/kanban/KanbanSwimlane.vue';
import CardDensityControl from '@/components/orders/kanban/CardDensityControl.vue';
import KanbanSwimlane from '@/components/orders/kanban/KanbanSwimlane.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { IconSearch } from '@tabler/icons-vue';
import Sortable from '@/utils/sortable';
import translator from '@/locale/translator';
import { debounce } from '@prospective/lithium';
import { useToast } from 'vue-toastification';

/* ---------------------------------- STATE --------------------------------- */
const props = defineProps<{ orderId: string }>();

const toast = useToast();

const DENSITY_STORAGE_KEY = 'kanban_card_density';
const VALID_DENSITIES: CardDensity[] = ['S', 'M'];

const lanes = ref<BoardLane[]>([]);
const collapsedLanes = reactive<Record<string, boolean>>({});
const isLoading = ref(true);
const loadError = ref(false);
const isMoving = ref(false);
const searchQuery = ref('');
const selectedIds = ref<string[]>([]);
// bumped to force-remount the lanes → rebuild DOM from data and reset Sortable/MultiDrag state
const boardVersion = ref(0);

/* --------------------------------- COMPUTED -------------------------------- */
const visibleLanes = computed(() => lanes.value.filter((lane) => !lane.is_hidden));

// remember the chosen density across visits
const stored = localStorage.getItem(DENSITY_STORAGE_KEY) as CardDensity | null;
const density = ref<CardDensity>(stored && VALID_DENSITIES.includes(stored) ? stored : 'M');
watch(density, (value) => localStorage.setItem(DENSITY_STORAGE_KEY, value));

/* --------------------------------- METHODS -------------------------------- */
const remountBoard = () => {
  boardVersion.value++;
};

const loadBoard = async () => {
  // only show the loading state / remount on the very first load; subsequent loads
  // (search, refresh) update in place so the board doesn't flash
  const isInitialLoad = lanes.value.length === 0;
  if (isInitialLoad) isLoading.value = true;
  loadError.value = false;

  try {
    const board = await fetchCandidateBoard(props.orderId, searchQuery.value.trim() || undefined);
    const incoming = board.swimlanes ?? [];

    // same swimlanes in the same order → keep the DOM and just swap the candidates/counts (no flash);
    // if the set of lanes actually changed, replace them and force a remount
    const sameLanes =
      incoming.length === lanes.value.length && incoming.every((lane, i) => lane.lane_key === lanes.value[i]?.lane_key);
    if (sameLanes) {
      const byKey = new Map(incoming.map((lane) => [lane.lane_key, lane]));
      for (const lane of lanes.value) {
        const source = byKey.get(lane.lane_key);
        if (!source) continue;
        lane.candidates = source.candidates;
        lane.count = source.count;
        lane.has_more = source.has_more;
      }
    } else {
      lanes.value = incoming;
      remountBoard();
    }

    selectedIds.value = [];
  } catch (error) {
    console.error('Failed to load candidate board:', error);
    loadError.value = true;
    if (!isInitialLoad) toast.error(translator.get('orders.details.kanban.load_error'));
  } finally {
    if (isInitialLoad) isLoading.value = false;
  }
};

/* --------------------------------- SEARCH --------------------------------- */
const loadBoardDebounced = debounce(loadBoard, 300);
watch(searchQuery, () => loadBoardDebounced());

/* -------------------------------- SELECTION ------------------------------- */
// selection is driven by the MultiDrag plugin (click + Cmd/Ctrl); we mirror it for the bulk bar
const handleSelectionChange = (ids: string[]) => {
  selectedIds.value = ids;
};

const clearSelection = () => {
  document.querySelectorAll<HTMLElement>('.candidate-card.card-selected').forEach((el) => Sortable.utils.deselect(el));
  selectedIds.value = [];
};

/* ---------------------------------- MOVE ---------------------------------- */
// find the lane a candidate currently sits in
const laneKeyOf = (candidateEntityId: string): string | null =>
  lanes.value.find((lane) => lane.candidates.some((c) => c.candidate_entity_id === candidateEntityId))?.lane_key ??
  null;

// move the given candidates to a target lane, optimistically, reverting if the API fails
const moveCandidatesTo = async (candidateEntityIds: string[], toLaneKey: string) => {
  const target = lanes.value.find((lane) => lane.lane_key === toLaneKey);
  if (!target) return;

  // only candidates that aren't already in the target lane actually move
  const movingIds = candidateEntityIds.filter((id) => {
    const from = laneKeyOf(id);
    return from !== null && from !== toLaneKey;
  });
  clearSelection();
  if (movingIds.length === 0) {
    remountBoard();
    return;
  }

  // snapshot for revert on failure
  const snapshot: BoardLane[] = JSON.parse(JSON.stringify(lanes.value));

  // optimistic local move
  const ids = new Set(movingIds);
  const moving: BoardLane['candidates'] = [];
  for (const lane of lanes.value) {
    if (lane.lane_key === toLaneKey) continue;
    for (let i = lane.candidates.length - 1; i >= 0; i--) {
      if (ids.has(lane.candidates[i].candidate_entity_id)) {
        moving.unshift(lane.candidates[i]);
        lane.candidates.splice(i, 1);
        lane.count = Math.max(0, lane.count - 1);
      }
    }
  }
  target.candidates.push(...moving);
  target.count += moving.length;
  remountBoard();

  isMoving.value = true;
  try {
    await moveCandidates(props.orderId, movingIds, toLaneKey, { skipDefaultErrorHandler: true });
  } catch (error) {
    console.error('Failed to move candidates:', error);
    lanes.value = snapshot; // snap back
    remountBoard();
    toast.error(translator.get('orders.details.kanban.move_error'));
  } finally {
    isMoving.value = false;
  }
};

const handleMove = (event: MoveEvent) => {
  moveCandidatesTo(event.candidateEntityIds, event.toLaneKey);
};
/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(() => {
  loadBoard();
});
</script>
<style scoped>
.kanban {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.kanban-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.kanban-search {
  width: 280px;
  max-width: 100%;
}

.kanban-search-icon {
  color: #98a2b3;
}

/* quick fix to remove the background color and display the icon as is */
:deep(.form-input .form-input-prefix) {
  background-color: transparent;
}

.kanban-bulkbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid #b2ddff;
  background: #eff8ff;
  border-radius: 8px;
}

.kanban-bulkbar-count {
  font-weight: 600;
  font-size: 14px;
  color: #101828;
}

.kanban-bulkbar-select {
  padding: 6px 10px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}

.kanban-bulkbar-clear {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #1570ef;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.kanban-bulkbar-clear:disabled {
  opacity: 0.5;
  cursor: default;
}

.kanban-board {
  display: flex;
  align-items: stretch;
  overflow: auto;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  /* header + toolbar */
  height: calc(100vh - 285px);
}

.kanban-board > :last-child {
  border-right: none;
}

.kanban-board.is-loading {
  opacity: 0.6;
  pointer-events: none;
}

.kanban-empty-board {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  min-height: calc(100vh - 240px);
}
</style>
